import { buildBaseURL } from '@warp-drive/utilities';
import type { Recommendation } from './recommendation.type.ts';
export interface RecommendationExtension extends Recommendation {}
export class RecommendationExtension {
  /*************************
   ** Computed Properties **
   *************************/
  get formattedInputSummary() {
    const input = this.inputSummary || {};
    const affordPayment = input['afford-payments'];
    const payoffGoal = input['payoff-goal'];
    const before2007 = input['federal-loan-time'];
    const currentlyWorking = input['current-working'];
    const monthsOfPayments = input['made-payments'];
    const currentForgivenessProgram = input['current-forgiveness-program'];
    const degree = input['academic-degree'];
    const employmentType = input['employment-type'];
    const planInGov = input['plan-in-government'];
    const planAsTeacher = input['plan-as-teacher'];
    const oneTimeAdjustmentPayments = input['made-limited-waiver-payments'];
    const income = input['household-income']; // in cents
    const familySize = input['household-size'];
    const state = input['residence-state'];

    return {
      affordPayment,
      payoffGoal,
      before2007,
      currentlyWorking,
      monthsOfPayments,
      currentForgivenessProgram,
      degree,
      employmentType,
      planInGov,
      planAsTeacher,
      oneTimeAdjustmentPayments,
      income,
      familySize,
      state,
    };
  }

  get hasErrorsInCalcs() {
    return this.hasErrorInCalcs;
  }

  get formattedCurrentPlanSummary() {
    // TODO: This can be removed so we just rely on currentPlanSummary directly
    // but would require changing code elsewhere because the keys are different
    const raw = this.currentPlanSummary;
    return this.formatPlanSummary(raw);
  }

  get formattedBestPlanSummary() {
    // TODO: This can be removed so we just rely on bestPlanSummary directly
    // but would require changing code elsewhere because the keys are different
    const raw = this.bestPlanSummary;
    return this.formatPlanSummary(raw);
  }

  get nextSteps() {
    return this.bestPlanNextSteps;
  }

  get bestPlanTableRowSummary() {
    return this.bestPlanNextSteps?.map((step) => step?.title).join(' - ');
  }

  get bestPlanHeaderSummary() {
    return this.bestPlanTableHeadings;
  }

  get currentPlanHeaderSummary() {
    return this.currentPlanTableHeadings;
  }

  get estimatedMonthlyPayment() {
    const monthlyCeiling = this.formattedBestPlanSummary?.monthlyPayments.sort(
      (a: number, b: number) => a - b
    )[0];
    const valueInCents = monthlyCeiling || 0;
    return valueInCents / 100;
  }

  get hasEstimatorResults() {
    return !!this.formattedBestPlanSummary;
  }

  get estimatedForgivenessAmount() {
    const remLoanCeiling = this.formattedBestPlanSummary?.remLoanBalance.sort((a, b) => a - b)[0];
    const valueInCents = remLoanCeiling || 0;
    return valueInCents / 100;
  }

  get estimatedForgivenessDate() {
    if (!this.formattedBestPlanSummary?.termMonths) {
      return '';
    }
    const termMonthsCeiling = this.formattedBestPlanSummary?.termMonths.sort(
      (a: number, b: number) => a - b
    )[0] as number;
    const currentYear = new Date().getFullYear();
    const approxYearsToRepay = Math.ceil(termMonthsCeiling / 12);
    return currentYear + approxYearsToRepay;
  }

  get estimatedMonthlySavings() {
    const bestPlanMonthlyCeiling = this.formattedBestPlanSummary?.monthlyPayments.sort(
      (a: number, b: number) => a - b
    )[0] as number;
    const currentPlanMonthlyCeiling = this.formattedCurrentPlanSummary?.monthlyPayments.sort(
      (a, b) => a - b
    )[0] as number;

    return (currentPlanMonthlyCeiling - bestPlanMonthlyCeiling) / 100;
  }

  get downloadUrl() {
    // WarpDrive URL construction (Phase 2.3) — same shape the legacy
    // adapter.buildURL produced: {host}/recommendations/{id}.pdf
    const base = buildBaseURL({
      identifier: { type: 'recommendations' },
      op: 'query',
      resourcePath: 'recommendations',
    });
    return `${base}/${this.id}.pdf`;
  }

  get downloadFilename() {
    return `tuitionio-${this.requestingApp || 'estimator'}-results.pdf`;
  }

  formatPlanSummary(rawPlanSummary: Record<string, unknown>) {
    if (!rawPlanSummary) {
      return;
    }
    // These are all arrays, even if a single object inside
    return {
      payoffAmount: rawPlanSummary['payoff-amount'],
      termMonths: rawPlanSummary['term-months'] as number[],
      monthlyPayments: rawPlanSummary['monthly-payments'] as number[],
      totalInterestPaid: rawPlanSummary['total-interest-paid'],
      remLoanBalance: rawPlanSummary['estimated-amount-forgiven'] as number[],
    };
  }
}

const Registration = {
  kind: 'object',
  name: 'recommendation',
  features: RecommendationExtension,
};
export default Registration;
